(() => {
  const roots = new WeakSet();

  const parseBoolean = (value) => value === 'true';

  const formatMoney = (amountInCents, currency) => {
    try {
      return new Intl.NumberFormat('es-MX', {
        style: 'currency',
        currency: currency || 'MXN',
        maximumFractionDigits: 0,
      }).format(amountInCents / 100);
    } catch (error) {
      return `$${Math.ceil(amountInCents / 100)}`;
    }
  };

  const initialiseWelcome = (root) => {
    if (!parseBoolean(root.dataset.popupEnabled)) return;

    const dialog = root.querySelector('[data-bordi-welcome-dialog]');
    if (!dialog) return;

    const closeButtons = dialog.querySelectorAll('[data-bordi-welcome-close]');
    const formFeedback = dialog.querySelector('[data-bordi-form-success], [data-bordi-form-error]');
    const frequencyDays = Math.max(Number(root.dataset.popupFrequency) || 14, 1);
    const storageKey = `bordi-welcome-dismissed-${root.dataset.sectionId || 'global'}`;
    let timer;

    const rememberDismissal = () => {
      try {
        window.localStorage.setItem(storageKey, String(Date.now()));
      } catch (error) {
        // Storage can be unavailable in private browsing; closing still works.
      }
    };

    const wasRecentlyDismissed = () => {
      try {
        const dismissedAt = Number(window.localStorage.getItem(storageKey));
        return dismissedAt && Date.now() - dismissedAt < frequencyDays * 86400000;
      } catch (error) {
        return false;
      }
    };

    const openDialog = () => {
      if (dialog.open) return;
      if (typeof dialog.showModal === 'function') dialog.showModal();
      else dialog.setAttribute('open', '');
      window.requestAnimationFrame(() => {
        (formFeedback || dialog.querySelector('input[type="email"], [data-bordi-copy-code], [data-bordi-welcome-close]'))
          ?.focus({ preventScroll: true });
      });
    };

    const closeDialog = () => {
      window.clearTimeout(timer);
      rememberDismissal();
      if (typeof dialog.close === 'function') dialog.close();
      else dialog.removeAttribute('open');
    };

    closeButtons.forEach((button) => button.addEventListener('click', closeDialog));
    dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      closeDialog();
    });
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog) closeDialog();
    });

    dialog.querySelectorAll('[data-bordi-copy-code]').forEach((button) => {
      button.addEventListener('click', async () => {
        const code = button.dataset.bordiCopyCode;
        if (!code) return;
        try {
          await navigator.clipboard.writeText(code);
          const label = button.querySelector('[data-bordi-copy-label]');
          if (label) label.textContent = 'Código copiado';
        } catch (error) {
          window.getSelection()?.selectAllChildren(button.querySelector('strong'));
        }
      });
    });

    if (formFeedback) {
      openDialog();
      return;
    }

    const shouldPreview = window.Shopify?.designMode && parseBoolean(root.dataset.popupEditorPreview);
    if (wasRecentlyDismissed() && !shouldPreview) return;
    timer = window.setTimeout(openDialog, shouldPreview ? 350 : Math.max(Number(root.dataset.popupDelay) || 7000, 500));
  };

  const initialiseShippingGoal = (root) => {
    if (!parseBoolean(root.dataset.shippingEnabled)) return;

    const threshold = Number(root.dataset.shippingThreshold);
    const template = root.querySelector('[data-bordi-shipping-template]');
    if (!template || !threshold || threshold <= 0) return;

    const currency = root.dataset.currency || 'MXN';
    const emptyText = root.dataset.shippingEmpty || 'Envío gratis desde [amount].';
    const pendingText = root.dataset.shippingPending || 'Te faltan [amount] para obtenerlo.';
    const unlockedText = root.dataset.shippingUnlocked || '¡Tu pedido ya tiene envío gratis!';
    let requestInFlight = false;
    let refreshPending = false;
    let refreshTimer;

    const createGoal = (context) => {
      const fragment = template.content.cloneNode(true);
      const goal = fragment.querySelector('[data-bordi-shipping-goal]');
      goal.dataset.bordiShippingContext = context;
      goal.hidden = false;
      return { fragment, goal };
    };

    const ensureGoals = () => {
      const placements = [
        {
          context: 'product',
          target: document.querySelector('main[data-template="product"] .product-form'),
          position: 'afterend',
        },
        {
          context: 'notification',
          target: document.querySelector('#cart-notification .cart-notification__links'),
          position: 'beforebegin',
        },
        {
          context: 'cart',
          target: document.querySelector('#main-cart-footer .cart__blocks'),
          position: 'afterbegin',
        },
        {
          context: 'drawer',
          target: document.querySelector('cart-drawer .drawer__footer'),
          position: 'afterbegin',
        },
      ];

      placements.forEach(({ context, target, position }) => {
        if (!target || document.querySelector(`[data-bordi-shipping-context="${context}"]`)) return;
        const { fragment } = createGoal(context);
        target.insertAdjacentElement
          ? target.insertAdjacentElement(position, fragment.firstElementChild)
          : target.appendChild(fragment);
      });
    };

    const renderGoals = (cart) => {
      const total = Number(cart?.total_price) || 0;
      const remaining = Math.max(threshold - total, 0);
      const progress = Math.min((total / threshold) * 100, 100);
      const isEmpty = total <= 0;

      ensureGoals();
      document.querySelectorAll('[data-bordi-shipping-goal]').forEach((goal) => {
        const message = goal.querySelector('[data-bordi-shipping-message]');
        const track = goal.querySelector('.bordi-shipping-goal__track');
        const isUnlocked = remaining === 0;

        goal.classList.toggle('is-unlocked', isUnlocked);
        goal.style.setProperty('--bordi-shipping-progress', `${progress}%`);
        if (message) {
          if (isUnlocked) message.textContent = unlockedText;
          else if (isEmpty) message.textContent = emptyText.replace('[amount]', formatMoney(threshold, currency));
          else message.textContent = pendingText.replace('[amount]', formatMoney(remaining, currency));
        }
        track?.setAttribute('aria-valuenow', String(Math.round(progress)));
      });
    };

    const refreshCart = async () => {
      if (requestInFlight) {
        refreshPending = true;
        return;
      }

      requestInFlight = true;
      do {
        refreshPending = false;
        try {
          const response = await fetch(`${window.routes?.cart_url || '/cart'}.js`, {
            credentials: 'same-origin',
            headers: { Accept: 'application/json' },
          });
          if (!response.ok) throw new Error('Cart request failed');
          renderGoals(await response.json());
        } catch (error) {
          // Conversion helpers should never interrupt the storefront.
        }
      } while (refreshPending);
      requestInFlight = false;
    };

    const scheduleRefresh = (delay = 0) => {
      window.clearTimeout(refreshTimer);
      refreshTimer = window.setTimeout(refreshCart, delay);
    };

    ensureGoals();
    scheduleRefresh();

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        const cartData = event?.cartData;
        if (cartData && Number.isFinite(Number(cartData.total_price)) && cartData.item_count !== undefined) {
          renderGoals(cartData);
        }
        scheduleRefresh(80);
      });
    }

    document.addEventListener('cart:lines-update', (event) => {
      const updatePromise = event?.detail?.promise || event?.promise;
      if (updatePromise && typeof updatePromise.then === 'function') {
        updatePromise.then(() => scheduleRefresh(), () => {});
      } else {
        scheduleRefresh(80);
      }
    }, true);

    document.addEventListener('cart:view', () => scheduleRefresh(), true);
    document.addEventListener('shopify:section:load', () => scheduleRefresh(60));
    window.addEventListener('pageshow', () => scheduleRefresh());
  };

  const initialiseRoot = (root) => {
    if (!root || roots.has(root)) return;
    roots.add(root);
    initialiseWelcome(root);
    initialiseShippingGoal(root);
  };

  const initialise = (scope = document) => {
    scope.querySelectorAll('[data-bordi-conversion-root]').forEach(initialiseRoot);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialise());
  } else {
    initialise();
  }

  document.addEventListener('shopify:section:load', (event) => initialise(event.target));
})();
